import { useState } from "react";
import PropTypes from 'prop-types';
import { C, S } from "../config/theme";
import { useOrgSettings } from "../hooks/OrgSettingsContext";

export function ConfirmModal({ modal, theme }) {
  if (!modal) return null;
  const T = theme;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 32, maxWidth: 420, width: "100%" }}>
        <h3 style={{ margin: "0 0 12px", color: T.textBold, fontSize: 18, fontWeight: 900 }}>{modal.title}</h3>
        <p style={{ color: T.textSec, fontSize: 14, lineHeight: 1.6, margin: "0 0 24px" }}>{modal.message}</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={S.btnOutline} onClick={modal.onCancel}>Cancel</button>
          <button style={{ ...S.btn, background: C.red }} onClick={modal.onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

ConfirmModal.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export function EventEditModal({ eventName, editValues, setEditValues, onSave, onDelete, onCancel, theme, isNew = false, newEventName, setNewEventName }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { boothTypes } = useOrgSettings();
  const T = theme;
  if (!eventName && !isNew) return null;
  const inputStyle = { width: "100%", boxSizing: "border-box", background: T.inputBg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "'Inter', sans-serif", outline: "none" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };
  const field = (label, key, type = "number") => (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={`field-${key}`} style={labelStyle}>{label}</label>
      <input id={`field-${key}`} type={type} value={editValues[key] || ""} placeholder="0" onChange={e => setEditValues(p => ({ ...p, [key]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))} style={inputStyle} />
    </div>
  );
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 32, maxWidth: 480, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <h3 style={{ margin: "0 0 20px", color: T.textBold, fontSize: 18, fontWeight: 900 }}>{isNew ? "Add New Event" : `Edit: ${eventName}`}</h3>
        {isNew && (
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Event Name</label>
            <input id="field-event-name" type="text" value={newEventName || ""} placeholder="e.g. AUSA 2026" onChange={e => setNewEventName(e.target.value)} style={inputStyle} aria-label="Event name" />
          </div>
        )}
        {field("Total Cost ($)", "total_cost")}
        {field("Influenced Pipeline ($)", "influenced_pipeline")}
        {field("Net New Opportunities ($)", "net_new_opportunities")}
        {field("Meetings Booked", "meetings")}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Account Team</label>
          <input type="text" value={editValues.segment || ""} placeholder="e.g. Army, Navy/USMC" onChange={e => setEditValues(p => ({ ...p, segment: e.target.value }))} style={inputStyle} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Booth Type</label>
          <select value={editValues.booth_size || ""} onChange={e => setEditValues(p => ({ ...p, booth_size: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }}>
            <option value="">— Select Booth Type —</option>
            {boothTypes.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
          </select>
        </div>
        {field("Event Date", "event_date", "date")}
        <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "space-between" }}>
          <div>{!isNew && onDelete && (
            confirmDelete
              ? <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "#f87171", fontWeight: 700 }}>Are you sure?</span>
                  <button style={{ ...S.btnOutline, borderColor: "#DC262655", color: "#f87171", fontSize: 12 }}
                    onClick={onDelete}>Yes, delete</button>
                  <button style={{ ...S.btnOutline, fontSize: 12 }}
                    onClick={() => setConfirmDelete(false)}>Cancel</button>
                </div>
              : <button style={{ ...S.btnOutline, borderColor: `${C.red}55`, color: C.red, fontSize: 12 }}
                  onClick={() => setConfirmDelete(true)}>Delete Event</button>
          )}</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.btnOutline} onClick={onCancel}>Cancel</button>
            <button style={S.btn} onClick={onSave}>{isNew ? "Create Event" : "Save Changes"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LoadingOverlay({ visible, message = "Saving to cloud...", darkMode, flame: Flame }) {
  if (!visible) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: `${darkMode ? "#0A1720" : "#000000"}AA`, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        {Flame && <Flame size={32} />}
        <p style={{ color: "#B5B9BC", marginTop: 12, fontSize: 14, fontWeight: 700 }}>{message}</p>
      </div>
    </div>
  );
}
